import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { BigButton } from '../components/BigButton';
import { useWorksheet } from '../context/WorksheetContext';
import {
  cancelSpeech,
  isReadAloudEnabled,
  isSpeechRecognitionAvailable,
  pauseSpeech,
  resumeSpeech,
  speak,
  startDictation,
  type DictationHandle,
} from '../lib/speech';
import { drawQuestionHighlight } from '../lib/render';
import { telemetry } from '../lib/telemetry';
import { cleanupAnswer } from '../lib/cleanup';
import { simplifyQuestion, type SimplifyResult } from '../lib/simplify';
import { predictNextWords } from '../lib/predict';
import { useExamMode } from '../context/ExamModeContext';
import { IconChevronLeft, IconChevronRight, IconMic } from '../components/Icons';

// Step 3: walk the user through every question on the worksheet.
//
// Features (post real-world testing):
//   - Read aloud uses a female voice where available (preferred over default robotic male).
//   - Pause / Stop controls during read-aloud.
//   - Settings toggle disables read-aloud entirely.
//   - After dictation, the raw transcript is cleaned via /api/answer-cleanup so the
//     speech-to-text engine's misheardings of proper nouns are restored against the
//     worksheet's own text content.
//   - Tap a question on the photo to jump straight to it.
//   - Big Previous / Next buttons at the bottom for explicit navigation.
export function AnswerScreen() {
  const navigate = useNavigate();
  const { state, setAnalysis, setAnswer, setCursor } = useWorksheet();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dictationHandleRef = useRef<DictationHandle | null>(null);
  // Base text that was already in the answer when the current dictation session started.
  // Used so a new dictation session APPENDS to the existing answer instead of overwriting it.
  const baseTextRef = useRef('');
  const [typingText, setTypingText] = useState('');
  const [listening, setListening] = useState(false);
  const [dictationError, setDictationError] = useState<string | null>(null);
  const [reading, setReading] = useState<'off' | 'speaking' | 'paused'>('off');
  const [cleaning, setCleaning] = useState(false);
  // Tap-to-place mode. When enabled, clicking the worksheet creates a new answer slot
  // at the tapped position. Critical for worksheets where the AI didn't detect a slot
  // (paragraphs with fill-in blanks, custom boxes, free-text exercises).
  const [placementMode, setPlacementMode] = useState(false);

  // Simplify state: per-question result cache + UI toggle for "show simplified".
  const [simplifyResults, setSimplifyResults] = useState<Record<string, SimplifyResult>>({});
  const [simplifyVisible, setSimplifyVisible] = useState<Record<string, boolean>>({});
  const [simplifyBusy, setSimplifyBusy] = useState(false);

  // Word prediction state: candidates for the current keystroke + abort controller.
  const [predictions, setPredictions] = useState<string[]>([]);
  const predictAbortRef = useRef<AbortController | null>(null);

  const { active: examModeActive, logEvent: logExamEvent } = useExamMode();

  const questions = state.analysis?.questions ?? [];
  const totalQuestions = questions.length;
  const currentQuestion = questions[state.cursor];
  const isManualMode = state.analysis?.source === 'manual';

  useEffect(() => {
    if (!state.image || !state.analysis) {
      navigate('/app/review', { replace: true });
    }
  }, [state.image, state.analysis, navigate]);

  // Sync the typing field with whatever's already saved for this question.
  useEffect(() => {
    if (currentQuestion) {
      setTypingText(state.answers[currentQuestion.id] ?? '');
    } else {
      setTypingText('');
    }
    setDictationError(null);
  }, [currentQuestion, state.answers]);

  // Draw the image and highlight the current question's answer box.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !state.image) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      if (currentQuestion) {
        drawQuestionHighlight(ctx, canvas.width, canvas.height, currentQuestion.answerBox);
      }
    };
    img.src = state.image;
  }, [state.image, currentQuestion]);

  // We deliberately do NOT auto-play the read-aloud on question change. Many users
  // prefer to read the question themselves first. Tapping the "Read aloud" button
  // starts it. This matches Sarah's real-world testing feedback (May 2026).

  // Stop any running speech / dictation when the question changes or screen unmounts.
  useEffect(() => {
    return () => {
      dictationHandleRef.current?.abort();
      dictationHandleRef.current = null;
      cancelSpeech();
      setReading('off');
    };
  }, [currentQuestion]);

  async function doSpeak(phrase: string) {
    setReading('speaking');
    if (examModeActive && currentQuestion) {
      logExamEvent('question_read_aloud', { question_id: currentQuestion.id });
    }
    await speak(phrase);
    // The promise resolves when the utterance ends OR when cancel is called.
    // We only flip to 'off' if we are still in 'speaking' state (not paused).
    setReading((curr) => (curr === 'speaking' ? 'off' : curr));
  }

  // Simplify the current question. Cached per question id.
  async function handleSimplify() {
    if (!currentQuestion) return;
    if (examModeActive) return; // Defensive: should already be hidden in exam mode.
    const id = currentQuestion.id;
    if (simplifyResults[id]) {
      setSimplifyVisible((v) => ({ ...v, [id]: !v[id] }));
      return;
    }
    setSimplifyBusy(true);
    try {
      const result = await simplifyQuestion({
        questionId: id,
        questionText: currentQuestion.questionText,
        context: state.analysis?.documentText,
        yearLevel: state.analysis?.yearLevel,
      });
      setSimplifyResults((r) => ({ ...r, [id]: result }));
      setSimplifyVisible((v) => ({ ...v, [id]: true }));
      telemetry.track('answer_method', { method: 'simplify_used' });
    } catch (e) {
      console.warn('Simplify failed', e);
    } finally {
      setSimplifyBusy(false);
    }
  }

  function handlePauseReading() {
    pauseSpeech();
    setReading('paused');
  }

  function handleResumeReading() {
    resumeSpeech();
    setReading('speaking');
  }

  function handleStopReading() {
    cancelSpeech();
    setReading('off');
  }

  function handleReadAgain() {
    if (!currentQuestion) return;
    void doSpeak(buildReadablePhrase(currentQuestion));
  }

  function startListening() {
    if (!isSpeechRecognitionAvailable()) {
      setDictationError('Speech recognition is not available on this browser. Type your answer instead.');
      return;
    }
    // Reading and dictating at the same time is jarring; stop reading first.
    cancelSpeech();
    setReading('off');

    // Capture whatever's already in the answer so the new dictation APPENDS instead of overwrites.
    baseTextRef.current = typingText.trim();

    setDictationError(null);
    setListening(true);
    telemetry.track('answer_method', { method: 'dictation' });
    dictationHandleRef.current = startDictation({
      onPartial: (text) => {
        const combined = baseTextRef.current ? `${baseTextRef.current} ${text}` : text;
        setTypingText(combined);
      },
      onFinal: async (text) => {
        setListening(false);
        if (!text || !currentQuestion) {
          // Nothing new dictated. Keep whatever the user already had.
          setTypingText(baseTextRef.current);
          return;
        }
        // Run the cleanup pass to fix misheard proper nouns AND convert spoken
        // maths (five plus seven → 5 + 7) against the worksheet text.
        // EXAM MODE skips cleanup entirely (JCQ: no AI correction allowed).
        if (examModeActive) {
          logExamEvent('answer_dictated', {
            question_id: currentQuestion.id,
            transcript_length: text.length,
          });
          const combined = baseTextRef.current ? `${baseTextRef.current} ${text}`.trim() : text;
          setTypingText(combined);
          baseTextRef.current = combined;
          return;
        }
        setCleaning(true);
        const result = await cleanupAnswer({
          transcript: text,
          questionText: currentQuestion.questionText,
          worksheetContext: state.analysis?.documentText,
          expectedFormat: currentQuestion.expectedFormat,
          options: currentQuestion.options,
        });
        setCleaning(false);
        const combined = baseTextRef.current
          ? `${baseTextRef.current} ${result.cleaned}`.trim()
          : result.cleaned;
        setTypingText(combined);
        // Reset the base so the NEXT dictation session appends from this new total.
        baseTextRef.current = combined;
        if (result.changed) {
          telemetry.track('answer_cleaned', { reason: result.reason ?? 'cleaned' });
        }
      },
      onError: (msg) => {
        setListening(false);
        setDictationError(msg);
      },
    });
  }

  // Maths symbol toolbar. Inserts the symbol at the cursor position in the answer field.
  function insertSymbol(symbol: string) {
    const el = textareaRef.current;
    if (!el) {
      setTypingText((t) => t + symbol);
      return;
    }
    const start = el.selectionStart ?? typingText.length;
    const end = el.selectionEnd ?? typingText.length;
    const next = typingText.slice(0, start) + symbol + typingText.slice(end);
    setTypingText(next);
    // Move cursor to just after the inserted symbol on the next tick.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + symbol.length;
      el.setSelectionRange(pos, pos);
    });
  }

  const MATH_SYMBOLS = ['+', '−', '×', '÷', '=', '(', ')', '²', '³', '√', 'π', '°'];

  function stopListening() {
    dictationHandleRef.current?.stop();
    dictationHandleRef.current = null;
    setListening(false);
  }

  const saveCurrent = useCallback(() => {
    if (currentQuestion) {
      const trimmed = typingText.trim();
      setAnswer(currentQuestion.id, trimmed);
      if (examModeActive && trimmed) {
        logExamEvent('answer_saved', {
          question_id: currentQuestion.id,
          length: trimmed.length,
        });
      }
    }
  }, [currentQuestion, setAnswer, typingText, examModeActive, logExamEvent]);

  // Word prediction. Debounced 350ms after the last keystroke. Disabled in exam mode.
  useEffect(() => {
    if (examModeActive || !currentQuestion) {
      setPredictions([]);
      return;
    }
    if (!typingText.trim() || typingText.trim().length < 2) {
      setPredictions([]);
      return;
    }
    if (predictAbortRef.current) predictAbortRef.current.abort();
    const ac = new AbortController();
    predictAbortRef.current = ac;
    const t = setTimeout(async () => {
      const results = await predictNextWords({
        questionText: currentQuestion.questionText,
        partial: typingText,
        expectedFormat: currentQuestion.expectedFormat,
        signal: ac.signal,
      });
      if (!ac.signal.aborted) setPredictions(results);
    }, 350);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [typingText, currentQuestion, examModeActive]);

  // Track question_displayed in exam mode on each cursor change.
  useEffect(() => {
    if (examModeActive && currentQuestion) {
      logExamEvent('question_displayed', { question_id: currentQuestion.id });
    }
  }, [examModeActive, currentQuestion, logExamEvent]);

  // Insert a predicted word at the end of the current answer.
  function insertPrediction(word: string) {
    const trimmed = typingText.trimEnd();
    const next = trimmed ? `${trimmed} ${word} ` : `${word} `;
    setTypingText(next);
    baseTextRef.current = next.trim();
    setPredictions([]);
    textareaRef.current?.focus();
  }

  function saveAndAdvance() {
    saveCurrent();
    if (state.cursor + 1 < totalQuestions) {
      setCursor(state.cursor + 1);
    } else {
      telemetry.track('worksheet_answers_complete', { questions: totalQuestions });
      navigate('/app/export');
    }
  }

  function goPrevious() {
    saveCurrent();
    if (state.cursor > 0) {
      setCursor(state.cursor - 1);
    } else {
      navigate('/app/review');
    }
  }

  // Canvas click handler: two modes.
  //   1. Normal (default): hit-test the click against each question's question + answer
  //      bounding boxes. If the user taps a question, jump straight to it.
  //   2. Placement: create a new answer slot at the tapped position. Used for blanks
  //      the AI did not detect (paragraphs with fill-ins, custom boxes).
  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    if (placementMode) {
      placeNewAnswerSlot(x, y);
      return;
    }

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const inAnswer =
        x >= q.answerBox.x &&
        x <= q.answerBox.x + q.answerBox.w &&
        y >= q.answerBox.y &&
        y <= q.answerBox.y + q.answerBox.h;
      const inQuestion =
        x >= q.questionBox.x &&
        x <= q.questionBox.x + q.questionBox.w &&
        y >= q.questionBox.y &&
        y <= q.questionBox.y + q.questionBox.h;
      if (inAnswer || inQuestion) {
        saveCurrent();
        setCursor(i);
        telemetry.track('answer_method', { method: 'tap_to_jump' });
        return;
      }
    }
  }

  // Add a fresh user-placed slot to the questions list. The new slot is sized to fit a
  // typical handwritten line (~15% of page width, ~3% of page height) and centred on
  // the tap point. The new question becomes the active one immediately.
  function placeNewAnswerSlot(x: number, y: number) {
    if (!state.analysis) return;
    saveCurrent();

    const slotWidth = 0.18; // 18% of page width
    const slotHeight = 0.035; // 3.5% of page height

    // Keep the slot fully inside the page.
    const clampedX = Math.max(0, Math.min(1 - slotWidth, x - slotWidth / 2));
    const clampedY = Math.max(0, Math.min(1 - slotHeight, y - slotHeight / 2));

    const id = `placed-${Date.now()}`;
    const newQuestion = {
      id,
      number: '',
      questionText: 'Your answer',
      questionBox: {
        x: clampedX,
        y: clampedY,
        w: slotWidth,
        h: slotHeight,
      },
      answerBox: {
        x: clampedX,
        y: clampedY,
        w: slotWidth,
        h: slotHeight,
      },
      answerType: 'line' as const,
      expectedFormat: 'short_phrase' as const,
    };

    const updated = {
      ...state.analysis,
      questions: [...state.analysis.questions, newQuestion],
    };
    setAnalysis(updated);
    setCursor(updated.questions.length - 1);
    setPlacementMode(false);
    telemetry.track('answer_method', { method: 'tap_to_place' });
  }

  const headerLabel = useMemo(() => {
    if (isManualMode) return 'Manual mode';
    if (totalQuestions === 0) return 'No questions detected';
    return `Question ${state.cursor + 1} of ${totalQuestions}`;
  }, [isManualMode, totalQuestions, state.cursor]);

  if (!state.image) return null;

  const isLastQuestion = state.cursor + 1 >= totalQuestions;

  return (
    <Screen>
      <div className="flex-1 flex flex-col gap-5 py-6">
        <header className="flex items-center justify-between">
          <button
            onClick={goPrevious}
            className="text-ink/70 text-sm font-medium hover:text-ink transition inline-flex items-center gap-1"
            aria-label={state.cursor > 0 ? 'Previous question' : 'Back to review'}
          >
            <IconChevronLeft size={16} />
            {state.cursor > 0 ? 'Previous question' : 'Back'}
          </button>
          <span className="text-sm text-ink/60 font-medium">{headerLabel}</span>
          <span className="w-20" />
        </header>

        <div className="bg-white rounded-2xl border border-ink/10 p-2 shadow-sm overflow-hidden">
          <div className={placementMode ? 'ring-4 ring-accent/40 rounded-xl' : ''}>
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              className={`w-full h-auto max-h-[40vh] object-contain rounded-xl ${
                placementMode ? 'cursor-crosshair' : 'cursor-pointer'
              }`}
              aria-label={
                placementMode
                  ? 'Tap where you want your answer to appear on the worksheet.'
                  : 'Worksheet. Tap a question to jump to it.'
              }
            />
          </div>
          <div className="flex items-center justify-between gap-2 px-1 py-1">
            {placementMode ? (
              <>
                <p className="text-[11px] text-accent font-medium">Tap the spot for your answer</p>
                <button
                  onClick={() => setPlacementMode(false)}
                  className="text-[11px] text-ink/50 hover:text-ink"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <p className="text-[11px] text-ink/40">
                  {totalQuestions > 1 ? 'Tap a question to jump there' : 'Tap to place an answer anywhere'}
                </p>
                <button
                  onClick={() => setPlacementMode(true)}
                  className="text-[11px] text-accent font-medium hover:underline"
                >
                  + Place an answer
                </button>
              </>
            )}
          </div>
        </div>

        {totalQuestions === 0 ? (
          <ManualOnlyHelp onRetake={() => navigate('/app/capture')} />
        ) : (
          <>
            <div className="bg-warm/60 rounded-2xl p-4 leading-relaxed">
              <p className="text-xs uppercase tracking-wide text-ink/50 mb-1">
                {currentQuestion?.number || `Question ${state.cursor + 1}`}
              </p>
              <p className="text-ink text-lg leading-snug">
                {currentQuestion?.questionText || 'Tap the slot you want to answer.'}
              </p>

              {/* Read-aloud controls. Off by default - user taps to start. */}
              {isReadAloudEnabled() && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                  {reading === 'speaking' && (
                    <>
                      <button
                        onClick={handlePauseReading}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-accent/30 text-accent hover:bg-accent/5 transition"
                      >
                        Pause reading
                      </button>
                      <button
                        onClick={handleStopReading}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-ink/15 text-ink/70 hover:bg-ink/5 transition"
                      >
                        Stop
                      </button>
                    </>
                  )}
                  {reading === 'paused' && (
                    <>
                      <button
                        onClick={handleResumeReading}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent text-white hover:brightness-105 transition"
                      >
                        Resume
                      </button>
                      <button
                        onClick={handleStopReading}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-ink/15 text-ink/70 hover:bg-ink/5 transition"
                      >
                        Stop
                      </button>
                    </>
                  )}
                  {reading === 'off' && (
                    <button
                      onClick={handleReadAgain}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent text-white text-sm hover:brightness-105 transition shadow-sm"
                    >
                      <span aria-hidden>▶</span> Read aloud
                    </button>
                  )}
                  {/* Simplify button - dyslexia accommodation. Hidden in exam mode. */}
                  {!examModeActive && currentQuestion && (
                    <button
                      onClick={handleSimplify}
                      disabled={simplifyBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-accent/30 text-accent text-sm hover:bg-accent/5 transition"
                    >
                      {simplifyBusy
                        ? 'Thinking...'
                        : simplifyVisible[currentQuestion.id]
                          ? 'Hide simpler'
                          : 'Make simpler'}
                    </button>
                  )}
                </div>
              )}

              {/* Simplified question card (only when 'Make simpler' has been used and is toggled on). */}
              {!examModeActive && currentQuestion && simplifyVisible[currentQuestion.id] && simplifyResults[currentQuestion.id] && (
                <div className="mt-3 bg-white/85 border border-accent/20 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wider text-accent font-semibold">
                    Simpler version
                  </p>
                  <p className="mt-1 text-ink/85 leading-relaxed text-sm whitespace-pre-line">
                    {simplifyResults[currentQuestion.id].simplified}
                  </p>
                  {simplifyResults[currentQuestion.id].isMultiPart &&
                    simplifyResults[currentQuestion.id].steps.length > 0 && (
                      <ol className="mt-2 pl-5 list-decimal space-y-1 text-sm text-ink/80">
                        {simplifyResults[currentQuestion.id].steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    )}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-ink/10 p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <label htmlFor="answer-input" className="text-xs uppercase tracking-wide text-ink/50">
                  Your answer
                </label>
                {typingText && (
                  <button
                    onClick={() => {
                      setTypingText('');
                      baseTextRef.current = '';
                    }}
                    className="text-xs text-ink/40 hover:text-ink/70 transition"
                  >
                    Clear
                  </button>
                )}
              </div>
              <textarea
                ref={textareaRef}
                id="answer-input"
                value={typingText}
                onChange={(e) => {
                  setTypingText(e.target.value);
                  baseTextRef.current = e.target.value;
                  if (examModeActive && currentQuestion) {
                    logExamEvent('answer_typed', { question_id: currentQuestion.id });
                  }
                }}
                onFocus={() => telemetry.track('answer_method', { method: 'typing' })}
                placeholder={listening ? 'Listening... say more to add to your answer' : 'Dictate or type here. Speak again to add more.'}
                rows={currentQuestion?.answerType === 'multi_line' || currentQuestion?.expectedFormat === 'paragraph' ? 5 : 3}
                className="w-full resize-none rounded-xl border border-ink/10 bg-cream px-3 py-2 text-ink leading-relaxed focus:outline-none focus:ring-4 focus:ring-accent/30"
              />

              {/* Word prediction chips. Disabled in exam mode (JCQ rules). */}
              {!examModeActive && predictions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-ink/40 self-center pr-1">
                    Suggest
                  </span>
                  {predictions.map((p, i) => (
                    <button
                      key={`${i}-${p}`}
                      type="button"
                      onClick={() => insertPrediction(p)}
                      className="px-3 py-1.5 rounded-full bg-accentSoft border border-accent/20 text-accent text-sm hover:bg-accent hover:text-white transition"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}

              {/* Maths symbol toolbar. Tap inserts at the cursor. Useful for sums and working. */}
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-ink/40 self-center pr-1">Maths</span>
                {MATH_SYMBOLS.map((symbol) => (
                  <button
                    key={symbol}
                    type="button"
                    onClick={() => insertSymbol(symbol)}
                    className="min-w-[34px] h-8 px-2 rounded-lg bg-cream border border-ink/10 text-ink hover:bg-accentSoft hover:border-accent/30 transition text-base font-medium"
                    aria-label={`Insert ${symbol}`}
                  >
                    {symbol}
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                {listening ? (
                  <BigButton variant="secondary" onClick={stopListening} className="!min-h-[52px]">
                    Stop listening
                  </BigButton>
                ) : (
                  <BigButton onClick={startListening} className="!min-h-[52px]">
                    <span className="inline-flex items-center gap-2 justify-center">
                      <IconMic size={18} />
                      {typingText ? 'Add more by voice' : 'Speak my answer'}
                    </span>
                  </BigButton>
                )}
              </div>
              {typingText && !listening && (
                <p className="text-[11px] text-ink/45 leading-relaxed">
                  Tip: tap "Add more by voice" to keep dictating - new words get added to what's already here.
                </p>
              )}

              {cleaning && (
                <p className="text-xs text-ink/55 inline-flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse" />
                  Checking the spelling against the worksheet...
                </p>
              )}

              {dictationError && (
                <p role="alert" className="text-sm text-amber-700">
                  {dictationError}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 mt-auto">
              <BigButton onClick={saveAndAdvance}>
                <span className="inline-flex items-center gap-2 justify-center">
                  {isLastQuestion ? 'Finish and export' : 'Next question'}
                  {!isLastQuestion && <IconChevronRight size={18} />}
                </span>
              </BigButton>
              {state.cursor > 0 && (
                <button
                  onClick={goPrevious}
                  className="text-center text-sm text-ink/60 hover:text-ink py-2 inline-flex items-center gap-1 justify-center"
                >
                  <IconChevronLeft size={14} /> Previous question
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}

function buildReadablePhrase(q: { number?: string; questionText: string; options?: string[] }): string {
  const parts: string[] = [];
  if (q.number) parts.push(q.number);
  parts.push(q.questionText);
  if (q.options && q.options.length) {
    parts.push('The options are:');
    parts.push(q.options.join(', '));
  }
  return parts.filter(Boolean).join('. ');
}

function ManualOnlyHelp({ onRetake }: { onRetake: () => void }) {
  return (
    <div className="bg-white rounded-2xl border border-ink/10 p-5 leading-relaxed">
      <h3 className="font-semibold text-ink mb-2">No questions detected automatically</h3>
      <p className="text-sm text-ink/70 mb-4">
        You can still answer this worksheet. Tap{' '}
        <strong className="text-accent">+ Place an answer</strong> above, then tap the spot on the
        worksheet where the answer should go. Repeat for each blank. Or retake the photo with AI
        on if you'd like automatic detection.
      </p>
      <BigButton variant="secondary" onClick={onRetake}>
        Retake with AI
      </BigButton>
    </div>
  );
}
